<template>
    <RouterLink class="my-ticket" :class="`my-ticket--${ticketType}`" :to="'/moje-tikety?tiket=' + id">
        <MyTicketIcon :legs :ticketType :betTypeRef />
        <div class="my-ticket__top">
            <div class="my-ticket__info">
                <div class="my-ticket__time-played">
                    <template v-if="ticketDate">
                        <span v-if="ticketDate.dayDate">{{ ticketDate?.dayDate }}</span>
                        <span v-if="ticketDate.time">{{ ticketDate?.time }}</span>
                        <span>{{ ticketName }}</span>
                    </template>
                </div>
                <MyTicketIcon :legs :ticketType :betTypeRef />
            </div>
            <h3 class="my-ticket__title">{{ ticketEventName }}</h3>
            <div class="my-ticket__text" v-html="ticketText"></div>
        </div>
        <hr />
        <div class="my-ticket__bottom">
            <div class="my-ticket__bottom-item">
                <span>Vklad</span>
                <strong>{{ formatPriceLite(stake, Number(stake) % 1 === 0 ? 0 : 2) }}</strong>
            </div>
            <div class="my-ticket__bottom-item">
                <span>Kurz</span>
                <strong>{{ Number(potentialPrice.decimal).toFixed(2) }}</strong>
            </div>
            <div class="my-ticket__bottom-item">
                <template v-if="bonusInfo">
                    <span>Výhra navíc</span>
                    <strong>{{ Number(bonusInfo.percentage) }} %</strong>
                </template>
            </div>
            <div class="my-ticket__bottom-item">
                <span>{{ isSettled ? "Výhra" : "Možná výhra" }}</span>
                <strong>{{ ticketWinnings }}</strong>
            </div>
        </div>
    </RouterLink>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import MyTicketIcon from "@/my-tickets/components/atoms/MyTicketIcon.vue";

import useUtils from "@/shared/composables/use-utils";
import { BetData, BetTypeRef } from "@/shared/model/bet";
import useTicketUtils from "@/my-tickets/composables/use-ticket-utils";
import { OutcomeResults } from "@/ticket/model/ticket-outcome-results";

const props = defineProps<BetData>();
const { getTicketType } = useTicketUtils();
const { formatPriceLite, formatDate, adjustCzechWordingByCount } = useUtils();
const ticketType = computed(() => getTicketType(props));

const ticketDate = computed(() => {
    return formatDate(props.placedDate);
});

const ticketName = computed(() => {
    return props.name != "Build Your Own System" ? props.name?.toUpperCase() : "KOMBI";
});

const ticketEventName = computed(() => {
    return props.legs?.[0].outcomes[0].outcomeDetails?.eventName;
});

const ticketWinnings = computed(() => {
    if (props.isSettled) {
        if (
            props?.legs.some((leg) =>
                leg?.outcomes.some((outcome) => outcome?.outcomeDetails?.result === OutcomeResults.VOID),
            )
        ) {
            return formatPriceLite(Number(props.winnings) + Number(props.refunds), 0);
        }
        return formatPriceLite(props.winnings);
    } else {
        return formatPriceLite(props.potentialPayout);
    }
});

const ticketText = computed(() => {
    const legsCount = props.legs?.length;
    const groupsCount = props.systemBets?.length;
    let legsText = "";
    let kombiText = "";

    if (legsCount > 1) {
        const additionalLegs = legsCount - 1;
        legsText = "a " + adjustCzechWordingByCount("prilezitost", additionalLegs);
    }

    if (groupsCount > 0) {
        if (groupsCount === 1) {
            //should never happen
            kombiText = `${groupsCount} skupina`;
        } else if (groupsCount >= 2 && groupsCount <= 4) {
            kombiText = `${groupsCount} skupiny`;
        } else {
            kombiText = `${groupsCount} skupin`;
        }
    }

    switch (props.betTypeRef) {
        case BetTypeRef.SINGLE:
            return `${props.legs?.[0].outcomes[0].outcomeDetails?.marketName}: <strong>
                ${props.legs?.[0].outcomes[0].outcomeDetails?.name}</strong>`;
        case BetTypeRef.KOMBI:
            return `${legsText}, ${kombiText}`;
        case BetTypeRef.AKO(legsCount):
            return legsText;
        case BetTypeRef.DOUBLE:
            return legsText; //TODO: does this differ smhw from AKO with 2 legs?
        case BetTypeRef.TRIPLE:
            return legsText; //TODO: does this differ smhw from AKO with 3 legs?
        default:
            return "";
    }
});
</script>
